/**
 * RadarChart — 축이 고정된 몇 개짜리 점수를 한눈에 보여주는 다각형 차트.
 *
 * 왜 ECharts 를 안 쓰나: 축이 3~8개로 고정이고 상호작용이 없는 정적 도형이라,
 * 차트 라이브러리를 끌어오면 얻는 것보다 잃는 게 많다(번들·테마 대응·인쇄).
 * SVG 를 직접 그리면 토큰 색을 그대로 쓰고 세 테마에서 저절로 맞는다.
 *
 * 좌표계 — 12시에서 시작해 시계방향. 축 라벨은 바깥쪽에 놓고, x 부호로
 * 정렬(왼쪽=end · 오른쪽=start · 위아래=middle)을 정한다.
 *
 * 값이 없는 축(null)은 0 으로 그리지 않는다 — 채점되지 않은 것과 0점은 다르다.
 * 그런 축은 라벨 아래 값 자리를 비우고, 다각형은 그 축에서 중심으로 붙는다.
 */
import { useId } from "react";

export interface RadarAxis {
  label: string;
  /** 0 ~ max. null 이면 미평가 */
  value: number | null;
}

export interface RadarChartProps {
  /** 3개 이상 — 12시부터 시계방향으로 놓인다 */
  axes: RadarAxis[];
  /** 축 최댓값 (기본 10) */
  max?: number;
  /** 안쪽 격자 고리 수 (기본 4) */
  rings?: number;
  /** 정사각 한 변 px — viewBox 기준이라 실제 크기는 CSS 가 정한다 (기본 360) */
  size?: number;
  /** 계열 색 — 토큰 문자열을 그대로 받는다 (기본 var(--navy)) */
  color?: string;
  /** 꼭짓점 값을 라벨 아래 적는다 (기본 true) */
  showValues?: boolean;
  /** 스크린리더용 요약 — 도형은 장식이고 수치는 옆 표에 있으므로 기본은 숨김 */
  label?: string;
}

const START = -Math.PI / 2;   // 12시

export function RadarChart({
  axes,
  max = 10,
  rings = 4,
  size = 360,
  color = "var(--navy)",
  showValues = true,
  label,
}: RadarChartProps) {
  const clipId = useId();
  const n = axes.length;
  if (n < 3) return null;

  const c = size / 2;
  // 라벨(한글 두 줄까지)이 들어갈 바깥 여백
  const pad = 58;
  const r = c - pad;

  const at = (i: number, radius: number) => {
    const a = START + (i / n) * Math.PI * 2;
    return [c + Math.cos(a) * radius, c + Math.sin(a) * radius] as const;
  };
  const poly = (radius: (i: number) => number) =>
    axes.map((_, i) => at(i, radius(i)).join(",")).join(" ");

  const valueAt = (i: number) => {
    const v = axes[i].value;
    if (v == null) return 0;
    return (Math.max(0, Math.min(max, v)) / max) * r;
  };

  return (
    <svg
      className="radar"
      viewBox={`0 0 ${size} ${size}`}
      role={label ? "img" : "presentation"}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      <defs>
        <clipPath id={clipId}>
          <polygon points={poly(() => r)} />
        </clipPath>
      </defs>

      {/* 격자 — 바깥에서 안으로 고리, 그리고 중심에서 뻗는 살 */}
      {Array.from({ length: rings }, (_, k) => (
        <polygon key={k} className="radar-ring" points={poly(() => (r * (rings - k)) / rings)} />
      ))}
      {axes.map((_, i) => {
        const [x, y] = at(i, r);
        return <line key={i} className="radar-spoke" x1={c} y1={c} x2={x} y2={y} />;
      })}

      {/* 계열 — 면은 옅게, 선과 꼭짓점은 또렷하게 */}
      <polygon
        className="radar-area"
        points={poly(valueAt)}
        style={{ fill: color, stroke: color }}
        clipPath={`url(#${clipId})`}
      />
      {axes.map((a, i) => {
        if (a.value == null) return null;
        const [x, y] = at(i, valueAt(i));
        return <circle key={i} className="radar-dot" cx={x} cy={y} r={3.5} style={{ fill: color }} />;
      })}

      {/* 축 라벨 — 바깥쪽. 값은 그 아래 한 줄 */}
      {axes.map((a, i) => {
        const [x, y] = at(i, r + 16);
        const dx = x - c;
        const anchor = Math.abs(dx) < 1 ? "middle" : dx > 0 ? "start" : "end";
        // 12시·6시는 글자가 도형에 닿지 않게 한 칸 더 민다
        const dy = y < c - r * 0.9 ? -4 : y > c + r * 0.9 ? 12 : 4;
        return (
          <g key={i}>
            <text className="radar-label" x={x} y={y + dy} textAnchor={anchor}>{a.label}</text>
            {showValues && (
              <text className="radar-value" x={x} y={y + dy + 15} textAnchor={anchor} style={{ fill: color }}>
                {a.value == null ? "–" : a.value}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}
